import { Dispatch, SetStateAction } from 'react';

import {
  isRenderingRepeatedSectionGroup,
  RenderingQuestionnaire,
  RenderingSection,
  RenderingSectionGroup,
} from '@zinnia/form-engine-sdk';

import { SectionGroupId } from '../../Helpers/questionnaireAnswers';

export enum Direction {
  next = 'next',
  previous = 'previous',
}

export type SectionGroupIndices = {
  id: string;
  indices: number[];
  sectionBlueprintIds: string[];
};

export type SectionGroupSelectionProps = {
  sectionGroup: RenderingSectionGroup;
  visitedSectionIndicesByGroup?: SectionGroupIndices[];
};

export type SectionNavigationData = {
  index: number;
  groupId: SectionGroupId;
  proposedInsuredIndex?: number;
  section?: RenderingSection;
};

export type NavigationRenderingSection = RenderingSection & { visited: boolean };

export type VisitedSectionProps = {
  activeSectionIndex: number;
  sectionIndicesByGroupId: string;
  setVisitedSectionsIndicesByGroup: Dispatch<SetStateAction<SectionGroupIndices[] | undefined>>;
  visitedSectionIndicesByGroup?: SectionGroupIndices[];
};

// Retrieves the section group from the rendering questionnaire based on the section group id and proposed insured id
export function findSectionGroup(
  renderingQuestionnaire: RenderingQuestionnaire,
  currentSectionGroupId: SectionGroupId,
  currentSelectedProposedInsuredIndex?: number,
): RenderingSectionGroup | undefined {
  return renderingQuestionnaire.find((renderingSectionGroup) => {
    if (
      currentSectionGroupId === SectionGroupId.insuredPeople &&
      isRenderingRepeatedSectionGroup(renderingSectionGroup) &&
      renderingSectionGroup.scope.parentId === SectionGroupId.insuredPeople
    ) {
      return renderingSectionGroup.scope.repetitionIndex === currentSelectedProposedInsuredIndex;
    }

    return renderingSectionGroup.id === currentSectionGroupId;
  });
}

// Generates id from section group id and proposed insured surrogateId that is used for storing and retrieving visited section indices
export function getSectionGroupWithIndicesId(sectionGroupId: SectionGroupId, surrogateId: string): string {
  return sectionGroupId === SectionGroupId.insuredPeople && !!surrogateId
    ? sectionGroupId + '.' + surrogateId
    : sectionGroupId;
}

export function getSectionGroupIdAndIndexFromSectionGroupWithIndicesId(sectionGroupWithIndicesId: string): {
  sectionGroupId: string;
  sectionGroupIndex: number | undefined;
} {
  const split = sectionGroupWithIndicesId.split('.');
  const indexString = split[1];
  const sectionGroupIndex = indexString ? Number(indexString) : undefined;
  return { sectionGroupId: split[0], sectionGroupIndex: sectionGroupIndex };
}

export type SectionIdsToVisibleSectionIndicesMap = Record<string, number>;

export function makeSectionIdsToVisibleSectionIndicesMapKey(
  sectionId: string,
  sectionGroupIdWithRepeatableIndex: string,
): string {
  return `${sectionId}_${sectionGroupIdWithRepeatableIndex}`;
}

export function getValuesFromSectionIdsToVisibleSectionIndicesMapKey(
  sectionIdsToVisibleSectionIndicesMapKey: string | undefined,
): {
  sectionId?: string;
  sectionGroupIdWithRepeatableIndex?: string;
} {
  const [sectionId, sectionGroupIdWithRepeatableIndex] = (sectionIdsToVisibleSectionIndicesMapKey || '').split('_');
  return {
    sectionId: sectionId || undefined,
    sectionGroupIdWithRepeatableIndex: sectionGroupIdWithRepeatableIndex || undefined,
  };
}

// Retrieves sections for a particular section group and computes visited and completed field values
export function getSectionsForSectionGroup(
  sectionGroupSections: RenderingSection[],
  visitedSectionIndices: number[],
): NavigationRenderingSection[] {
  return sectionGroupSections
    .filter((section) => section.visible)
    .map((section, sectionIndex) => {
      return {
        ...section,
        visited: visitedSectionIndices.includes(sectionIndex),
        completed: section.completed,
      };
    });
}

// Checks to see if current section index has been added to the visitedSectionIndicesByGroup collection and adds it if not
export function updateVisitedSectionIndices(visitedSectionProps: VisitedSectionProps): void {
  const {
    activeSectionIndex,
    sectionIndicesByGroupId,
    setVisitedSectionsIndicesByGroup,
    visitedSectionIndicesByGroup,
  } = visitedSectionProps;

  // First index to be added
  if (!visitedSectionIndicesByGroup) {
    return setVisitedSectionsIndicesByGroup([
      { id: sectionIndicesByGroupId, indices: [activeSectionIndex], sectionBlueprintIds: [] },
    ]);
  }

  const sectionGroup = visitedSectionIndicesByGroup.find((sectionGroup) => sectionGroup.id === sectionIndicesByGroupId);

  // First index in section group to be added
  if (!sectionGroup) {
    setVisitedSectionsIndicesByGroup((prevState) => [
      ...(prevState || []),
      { id: sectionIndicesByGroupId, indices: [activeSectionIndex], sectionBlueprintIds: [] },
    ]);

    // New index to add in section group
  } else if (!sectionGroup.indices.includes(activeSectionIndex)) {
    sectionGroup.indices.push(activeSectionIndex);
    setVisitedSectionsIndicesByGroup((prevState) => {
      return prevState?.map((group) => (group.id === sectionIndicesByGroupId ? sectionGroup : group));
    });
  }
}
